import { TagListItem } from '@/common/components/molecules/tagListInput/tagListInput.types';

export const getStringWidth = (text: string, fontSize?: number) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context && fontSize) {
    context.font = `${fontSize}px 'Pretendard'`;
  }
  const { width = 0 } = context?.measureText(text) ?? {};
  return Math.ceil(width);
};

export const getTagWidth = (text: string, tagMaxWidth: number, fontSize?: number) => {
  const stringWidth = getStringWidth(text, fontSize);
  return Math.min(tagMaxWidth, stringWidth);
};

export interface VisibleTagItemParam<ValueType> {
  wrapperEl: HTMLElement;
  itemList: TagListItem<ValueType>[];
  options?: {
    tagPadding?: number;
    wrapperPadding?: number;
    tagMaxWidthPercent?: number;
    fontSize?: number;
    /**
     * `wrapperEl.clientWidth` 대신 사용할 기준 폭.
     * 측정 대상 폭이 계산 결과(표시 태그 수·`+N` 배지 유무)에 따라 변하면
     * 계산 → 렌더 → 재계산 되먹임이 생기므로, 호출부가 결과와 무관한 폭을 직접 넘길 수 있게 한다.
     */
    availableWidth?: number;
  };
}

export const getVisibleTagItems = <ValueType>({
  wrapperEl,
  itemList,
  options,
}: VisibleTagItemParam<ValueType>) => {
  if (!wrapperEl || !itemList?.length) {
    return [];
  }

  const wrapperWidth = options?.availableWidth ?? wrapperEl.clientWidth;
  const {
    tagPadding = 20,
    wrapperPadding = 20,
    tagMaxWidthPercent = 0.8,
    fontSize,
  } = options ?? {};
  const tagMaxWidth = Math.ceil(wrapperWidth * tagMaxWidthPercent);

  let totalWidth = wrapperPadding * 2;
  let visibleTagIndex = 0;

  for (let i = 0; i < itemList.length; i++) {
    const item = itemList[i];

    const tagWidth = getTagWidth(item.name, tagMaxWidth, fontSize);
    const expectedTotalWidth = tagWidth + tagPadding + totalWidth;

    if (expectedTotalWidth <= wrapperWidth) {
      visibleTagIndex++;
      totalWidth += tagWidth + tagPadding;
    } else {
      break;
    }
  }

  let lastIndex = visibleTagIndex < 0 ? itemList.length : visibleTagIndex;

  if (lastIndex < itemList.length) {
    const moreCountWidth =
      getTagWidth(`+ ${itemList.length - lastIndex}`, tagMaxWidth, fontSize) + tagPadding;

    if (lastIndex > 1 && totalWidth + moreCountWidth >= wrapperWidth) {
      lastIndex -= 1;
    } else if (lastIndex === 0) {
      lastIndex = 1;
    }
  }

  return itemList?.slice(0, lastIndex);
};

export const getVisibleTagItemsInOrder = <ValueType>({
  wrapperEl,
  itemList,
  options,
}: VisibleTagItemParam<ValueType>) => {
  if (!wrapperEl || !itemList?.length) {
    return [];
  }

  const wrapperWidth = options?.availableWidth ?? wrapperEl.clientWidth;
  const {
    tagPadding = 20,
    wrapperPadding = 20,
    tagMaxWidthPercent = 0.8,
    fontSize,
  } = options ?? {};
  const tagMaxWidth = Math.ceil(wrapperWidth * tagMaxWidthPercent);

  let totalWidth = wrapperPadding * 2;
  let visibleTagIndex = 0;

  for (let i = 0; i < itemList.length; i++) {
    const item = itemList[i];

    const tagWidth = getTagWidth(item.name, tagMaxWidth, fontSize);
    const expectedTotalWidth = tagWidth + tagPadding + totalWidth;

    if (expectedTotalWidth <= wrapperWidth) {
      visibleTagIndex++;
      totalWidth += tagWidth + tagPadding;
    } else {
      break;
    }
  }

  let lastIndex = visibleTagIndex < 0 ? itemList.length : visibleTagIndex;

  if (lastIndex < itemList.length) {
    const moreCountWidth =
      getTagWidth(`+ ${itemList.length - lastIndex}`, tagMaxWidth, fontSize) + tagPadding;

    if (lastIndex > 1 && totalWidth + moreCountWidth >= wrapperWidth) {
      lastIndex -= 1;
    } else if (lastIndex === 0) {
      lastIndex = 1;
    }
  }

  return itemList?.slice(0, lastIndex);
};
